import { Vector2 } from "../../math";
import * as a1lib from "alt1";
import { ImgRefBind, ImgRefData } from "alt1";
import { LegacyOverlayGeometry } from "../LegacyOverlayGeometry";
import { Alt1Color } from "../Alt1Color";
import { Alt1OverlayDrawCalls } from "../overlay/Alt1OverlayDrawCalls";
import { FakeLodash } from "../../coreutil/FakeLodash";
export class CapturedImage {
    constructor(capture, screen_rectangle, parent = null) {
        this.capture = capture;
        this.screen_rectangle = screen_rectangle;
        this.parent = parent;
        this._name = undefined;
        this._data = undefined;
        this._fullCapturedRectangle = {
            origin: { x: capture.img_ref.x, y: capture.img_ref.y },
            size: { x: capture.img_ref.width, y: capture.img_ref.height }
        };
        if (!this.screen_rectangle) {
            this.screen_rectangle = this._fullCapturedRectangle;
        }
        if (parent) {
            this._relativeRectangle = {
                origin: Vector2.sub(this.screen_rectangle.origin, this.parent.screen_rectangle.origin),
                size: screen_rectangle.size
            };
        }
        this.size = this.screen_rectangle.size;
    }
    ensure_current() {
        if (this.capture.img_ref instanceof ImgRefBind && this.root() != CapturedImage.latest_capture) {
            //debugger
            throw new Error("Tried to perform an operation on an expired ImgRefBind");
        }
    }
    timestamp() {
        return this.capture.timestamp;
    }
    setName(name) {
        this._name = name;
        return this;
    }
    name() {
        return this._name;
    }
    raw() {
        return this.capture.img_ref;
    }
    screenRectangle() {
        return this.screen_rectangle;
    }
    relativeRectangle() {
        return this._relativeRectangle;
    }
    findNeedleSet(needle_set, limit_one) {
        const found = [];
        for (let i = 0; i < needle_set.needles.length && (!limit_one || found.length == 0); i++) {
            found.push(...this.findNeedle(needle_set.needles[i]));
        }
        return found;
    }
    findNeedle(needle) {
        const find = (() => {
            if (this.capture.img_ref instanceof a1lib.ImgRefBind && alt1.bindFindSubImg) {
                this.ensure_current();
                // Happy path: Accelerated image lookup via Alt1 is available
                const r = alt1.bindFindSubImg(this.capture.img_ref.handle, needle.encoded(), needle.underlying.width, this.screen_rectangle.origin.x, this.screen_rectangle.origin.y, this.screen_rectangle.size.x, this.screen_rectangle.size.y);
                if (!r) {
                    throw new a1lib.Alt1Error();
                }
                return JSON.parse(r);
            }
            else {
                // Fallback:
                const found = a1lib.ImageDetect.findSubbuffer(this.getData(), needle.underlying);
                return found.map(p => Vector2.add(this.screen_rectangle.origin, p));
            }
        });
        return find().map(pos => {
            return this.getScreenSection({ origin: pos, size: { x: needle.underlying.width, y: needle.underlying.height } });
        });
    }
    root() {
        if (this.parent)
            return this.parent.root();
        else
            return this;
    }
    isFullScreen() {
        return this.root() == this;
    }
    getScreenSection(absolute_area) {
        return new CapturedImage(this.root().capture, FakeLodash.cloneDeep(absolute_area), this.root());
    }
    getSubSection(relative_rectangle) {
        return new CapturedImage(this.capture, {
            origin: Vector2.add(this.screen_rectangle.origin, relative_rectangle.origin),
            size: relative_rectangle.size
        }, this);
    }
    getData() {
        if (!this._data) {
            this.ensure_current();
            this._data = this.capture.img_ref.toData(this.screen_rectangle.origin.x, this.screen_rectangle.origin.y, this.screen_rectangle.size.x, this.screen_rectangle.size.y);
        }
        return this._data;
    }
    recapture(img) {
        return img.getScreenSection(this.screenRectangle());
    }
    static capture(section = null) {
        try {
            // TODO: This should respect a1.captureInterval in some way
            const timestamp = Date.now();
            // Default to the full rs game window if no specific section was specified
            const area = section ?? { origin: { x: 0, y: 0 }, size: { x: alt1.rsWidth, y: alt1.rsHeight } };
            const img = a1lib.captureHold(area.origin.x, area.origin.y, area.size.x, area.size.y);
            return this.latest_capture = new CapturedImage({ img_ref: img, timestamp: timestamp }, area)
                .setName(section ? "Partial Capture" : "Full Capture");
        }
        catch (e) {
            console.error(`Capture failed: ${e.message}`);
            console.error(e.stack);
            return null;
        }
    }
    debugOverlay(overlay = new LegacyOverlayGeometry()) {
        overlay.rect2(this.screenRectangle());
        if (this._name) {
            overlay.text(this._name, this.screen_rectangle.origin, { width: 10, centered: false, color: Alt1Color.white });
        }
        return overlay;
    }
    debugOverlay2(overlay = new Alt1OverlayDrawCalls.GeometryBuilder) {
        overlay.rectangle(this.screenRectangle());
        if (this._name) {
            overlay.text(this._name, this.screen_rectangle.origin, { width: 10, centered: false, color: Alt1Color.white });
        }
        return overlay;
    }
}
CapturedImage.latest_capture = null;
(function (CapturedImage) {
    function bind(img) {
        return new CapturedImage({ timestamp: Date.now(), img_ref: new ImgRefData(img) }, { origin: { x: 0, y: 0 }, size: { x: img.width, y: img.height } });
    }
    CapturedImage.bind = bind;
})(CapturedImage || (CapturedImage = {}));
